import { filter, ifElse, map, reduceRight } from 'ramda';

export interface TransitMap {
  [key: string]: BasicTransit;
}

interface NodeMap {
  [key: string]: AnyWorkflowNode;
}

interface RunTime {
  [extensionName: string]: { [moduleID: string]: any };
}
interface ModuleMetadata {
  extension: {
    displayName: string;
    name: string;
    objectUUID: string;
  };
  module: {
    id: string;
    name: string;
  };
  output?: any;
}

export interface ChildWorkflow {
  workflow: string;
  stateValues?: { [key: string]: any };
  iterator?: string;
}

export interface BasicTransit {
  state: string;
  runModules: BasicModule[];
  nextState: string[];
  question?: string;
  answer?: string;
  checkValue?: string[];
  ctxToParse?: string[];
  entities?: { [key: string]: string };
  groupName?: string;
  runTime?: RunTime;
  childConversation?: string;
  childInput?: { [key: string]: any };
  childWorkflow?: ChildWorkflow;
  childOutput?: any;
  childRan?: boolean;
}

export interface BasicModule {
  groupName?: string;
  moduleData?: any;
  isExecuted: boolean;
  moduleID: string;
  pluginUUID: string;
  metadata: ModuleMetadata;
}

export interface AnyWorkflowNode<
  T extends string = any,
  M = any,
  N extends AnyWorkflowNode<any> = any
> {
  id: string;
  next: N[];
  type: T;
  meta: M;
}

export type TransitWorkflowNode = AnyWorkflowNode<'TransitStep', BasicTransit>;
export type ModuleWorkflowNode = AnyWorkflowNode<'ModuleStep', BasicModule>;
export type JunctionWorkflowNode = AnyWorkflowNode<
  'JunctionStep',
  BasicTransit,
  TransitWorkflowNode
>;
export type GroupWorkflowNode = AnyWorkflowNode<
  'GroupStep',
  AnyWorkflowNode,
  TransitWorkflowNode | JunctionWorkflowNode | GroupWorkflowNode
>;
export type GroupExitWorkflowNode = AnyWorkflowNode<
  'GroupExitStep',
  AnyWorkflowNode,
  never
>;
export type ChildWorkflowNode = AnyWorkflowNode<
  'ChildStep',
  BasicTransit,
  TransitWorkflowNode | JunctionWorkflowNode
>;

export type AllWorkflowNodes =
  | TransitWorkflowNode
  | ModuleWorkflowNode
  | JunctionWorkflowNode
  | GroupWorkflowNode
  | ChildWorkflowNode;

type BuildNodeFn = (id: string) => AnyWorkflowNode | undefined;

const getModMetadata = (
  metadata: ModuleMetadata = {
    extension: {
      displayName: '',
      name: '',
      objectUUID: '',
    },
    module: {
      name: '',
      id: '',
    },
  },
  runTime?: RunTime
): ModuleMetadata => {
  let output: any;
  if (
    runTime !== undefined &&
    runTime[metadata.extension.name] !== undefined &&
    runTime[metadata.extension.name][metadata.module.id] !== undefined
  ) {
    output = runTime[metadata.extension.name][metadata.module.id];
  }
  return {
    output,
    ...metadata,
  };
};

export const workflowTreeBuilder = (transitMap: TransitMap) => {
  const visitedNodes: NodeMap = {};

  const hasChildConversation = (id: string) => {
    const transit = transitMap[id];
    if (transit === undefined) {
      return false;
    }

    return (
      (transit.childConversation !== undefined &&
        transit.childConversation !== '') ||
      (transit.childWorkflow !== undefined &&
        transit.childWorkflow.workflow !== '')
    );
  };

  const hasGroupName = (id: string) =>
    transitMap[id] !== undefined &&
    transitMap[id].groupName !== undefined &&
    transitMap[id].groupName !== '';

  const isGroupName = (groupName: string) => (id: string) =>
    hasGroupName(id) && transitMap[id].groupName === groupName;

  const hasVisited = (id: string) => visitedNodes[id] !== undefined;
  const getVisited = (id: string) => visitedNodes[id];
  const memoizeNode = (fn: (...args: any[]) => any) =>
    ifElse(hasVisited, getVisited, fn);

  const buildModuleNode = (prefix: string, runTime?: RunTime) =>
    reduceRight(
      (mod: BasicModule, trees: AnyWorkflowNode[]): AnyWorkflowNode[] => [
        {
          id: `${prefix}.${
            mod.metadata !== undefined ? mod.metadata.extension.name : 'unknown'
          }.${mod.moduleID}`,
          type: 'ModuleStep',
          next: trees,
          meta: { ...mod, metadata: getModMetadata(mod.metadata, runTime) },
        },
      ]
    );

  const buildTreeWith = (builderFn?: BuildNodeFn) => {
    return memoizeNode((id: string): AnyWorkflowNode => {
      const transit = transitMap[id];
      const nextState = transit.nextState || [];
      const buildModules = buildModuleNode(id, transit.runTime);

      visitedNodes[transit.state] = {
        id: transit.state,
        type: hasChildConversation(id)
          ? 'ChildStep'
          : nextState.length > 1
          ? 'JunctionStep'
          : 'TransitStep',
        meta: transit,
        next: [],
      };
      visitedNodes[transit.state].next = buildModules(
        filter(
          (n: any) => n !== undefined,
          map(builderFn || buildTree, nextState)
        ) as AnyWorkflowNode[],
        transit.runModules
      );
      return visitedNodes[transit.state];
    });
  };

  const buildGroupNode = (id: string): GroupWorkflowNode => {
    const groupName = transitMap[id].groupName as string;
    const isPartOfGroup = isGroupName(groupName);
    // let nextIds: string[] = [];
    let next: AnyWorkflowNode[] = [];

    visitedNodes[groupName] = {
      id: groupName,
      type: 'GroupStep',
      meta: {
        nodeTree: {},
        transitMap: {
          [id]: transitMap[id],
        },
      },
      next: [],
    };

    const buildTreeUntilNotGroup = buildTreeWith((nextId: string) => {
      if (isPartOfGroup(nextId)) {
        return buildTreeUntilNotGroup(nextId);
      }
      const nextNode = buildTree(nextId);
      next = [...next, nextNode];
      const exitNode: GroupExitWorkflowNode = {
        id: nextId,
        type: 'GroupExitStep',
        meta: nextNode,
        next: [],
      };
      return exitNode;
    });

    visitedNodes[groupName].meta = buildTreeUntilNotGroup(id);
    visitedNodes[groupName].next = next;

    return visitedNodes[groupName] as GroupWorkflowNode;
  };

  const buildTransitNode = buildTreeWith();
  const buildTree = ifElse(hasGroupName, buildGroupNode, buildTransitNode);

  return {
    buildTree,
    buildTreeWith,
    buildTransitNode,
    buildModuleNode,
    buildGroupNode,
  };
};

export const buildWorkflowTree = ({
  transitMap,
  startState,
}: {
  transitMap: TransitMap;
  startState: string;
}): AnyWorkflowNode => {
  const { buildTree } = workflowTreeBuilder(transitMap);

  return buildTree(startState);
};
